// Task board screen (M55.4, ARCH §30.3, COW-3). A small modal: add a line, click
// one to toggle done (struck through + dimmed), delete the first with its own
// button. Client-only (ENG-1) — the same modal shape as `ui/screens/sign.ts`.
//
// Hangar §39.7 (M107.3) — **Link to Hangar Reminders…** (bridged only, `linkOptions`).
// This screen never imports `hangar/**` itself (the button's click handler is supplied
// already-bound by the caller, which dynamically imports `hangar/boardlink` — the same
// NET-7 gate every other `hangar/**` entry point uses) and never calls `reconcileBoard`
// directly: `linkOptions.onLink`/`isLinked` are the whole surface this file touches.
import { h } from '../h';
import { COW } from '../../../sim/config';
import { addTask, toggleTask, deleteTask, type TaskLine } from '../../../sim/coworking/text';
import { newId } from '../../world/doors';

export interface TaskBoardLinkOptions {
  /** Whether this cell already has an active Hangar Reminders link. */
  isLinked: boolean;
  /** The player pressed the link/unlink button. Resolves once the server + client
   *  storage both agree — the caller re-renders this screen's link state from it. */
  onToggleLink(): Promise<void>;
}

/** Open the task board over `current`; `onChange` fires with the new lines on
 *  every add/toggle/delete (so a shared board syncs live, not just on close).
 *  Returns a close handle. */
export function openTaskBoard(
  current: readonly TaskLine[],
  onChange: (lines: TaskLine[]) => void,
  linkOptions?: TaskBoardLinkOptions,
): () => void {
  let lines = current.map((l) => ({ ...l }));
  const list = h('div', { class: 'bb-board-list' });
  const input = h('input', { class: 'bb-board-input', type: 'text', maxLength: COW.LINE_MAX, placeholder: 'New task…' });
  input.style.cssText = 'display:block;margin:8px 0;padding:4px;width:220px;';
  const add = h('button', { class: 'bb-btn' }, 'Add');
  const close_ = h('button', { class: 'bb-btn' }, 'Close');
  const panelChildren: HTMLElement[] = [h('div', { class: 'bb-board-title' }, 'Task Board'), list, input, add];

  let link: HTMLElement | null = null;
  let linkNote: HTMLElement | null = null;
  if (linkOptions) {
    link = h(
      'button',
      {
        class: 'bb-btn',
        'data-testid': 'board-link-toggle',
        onclick: () => {
          link!.setAttribute('disabled', 'true');
          void linkOptions.onToggleLink().finally(() => link!.removeAttribute('disabled'));
        },
      },
      linkOptions.isLinked ? 'Unlink from Hangar Reminders' : 'Link to Hangar Reminders…',
    );
    panelChildren.push(link);
    if (linkOptions.isLinked) {
      // §39.7's own binding copy: the one thing this feature does NOT do is keep syncing
      // once the tab/game is closed — said plainly rather than left to be discovered.
      linkNote = h('div', { class: 'bb-board-link-note', 'data-testid': 'board-link-note' }, 'Syncs while TerraForge is open.');
      panelChildren.push(linkNote);
    }
  }
  panelChildren.push(close_);

  const panel = h('div', { class: 'bb-board-editor' }, ...panelChildren);
  panel.style.cssText = 'background:#2b2b2b;color:#fff;padding:16px;border:2px solid #000;text-align:center;';
  const overlay = h('div', { class: 'bb-modal bb-board-modal' }, panel);
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:#0006;';
  document.body.append(overlay);

  const render = (): void => {
    list.replaceChildren(...lines.map((l, i) => {
      const text = h('span', { onclick: () => { lines = toggleTask(lines, i); onChange(lines); render(); } }, l.text);
      text.style.cssText = `cursor:pointer;${l.done ? 'text-decoration:line-through;opacity:0.5;' : ''}`;
      const del = h('button', { class: 'bb-btn', onclick: () => { lines = deleteTask(lines, i); onChange(lines); render(); } }, '✕');
      del.style.cssText = 'margin-left:8px;';
      return h('div', { class: 'bb-board-row' }, text, del);
    }));
  };
  render();

  add.addEventListener('click', () => {
    if (!input.value.trim()) return;
    lines = addTask(lines, input.value, newId('line'));
    onChange(lines);
    input.value = '';
    render();
  });
  const closeFn = (): void => { overlay.remove(); };
  close_.addEventListener('click', closeFn);
  return closeFn;
}
